import axios from 'axios';
import { DeviceConfigService } from '../services/deviceConfigService';

const IS_ON_PREM = process.env.REACT_APP_DEPLOYMENT_MODE === 'on-prem';

/**
 * Get current auth token — refreshes via loginByIp (on-prem) or
 * stored credentials (cloud) if expired.
 */
// Keep in-request logins short: a request should not hang for minutes while
// the auth server is down. The App-level retry loop handles longer outages.
const REQUEST_LOGIN_RETRY = { maxRetries: 3 };

const getAuthToken = async (): Promise<string | null> => {
  try {
    const token = await DeviceConfigService.refreshTokenIfNeeded(REQUEST_LOGIN_RETRY);
    return token;
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return null;
  }
};

/**
 * Shared 401 retry handler — used by both flightApi and dcmmApi interceptors.
 *
 * On-prem: re-calls loginByIp (stateless — no stored credentials to clear).
 *          If that fails, just reject — do NOT clear credentials or reload,
 *          as the device has no manual config to fall back to.
 *
 * Cloud:   re-calls login with stored apiKey+secretKey.
 *          Credentials are cleared (and the page reloaded to re-configure)
 *          ONLY when the auth server rejects them with 401/403. Network
 *          errors, timeouts and 5xx keep the credentials so the device
 *          recovers on its own once the server is back.
 */
const handle401Retry = async (
  originalRequest: any,
  apiInstance: ReturnType<typeof axios.create>,
): Promise<any> => {
  originalRequest._retry = true;

  try {
    // reLogin() branches internally and is shared by concurrent 401s:
    //   On-prem → POST /device-auth/loginByIp  (IP re-verified on backend)
    //   Cloud   → POST /device-auth/login      (apiKey + secretKey)
    const { token } = await DeviceConfigService.reLogin(REQUEST_LOGIN_RETRY);
    originalRequest.headers.Authorization = `Bearer ${token}`;
    return apiInstance(originalRequest);
  } catch (refreshError: any) {
    const status = refreshError?.status;
    if (IS_ON_PREM) {
      // On-prem: nothing to clear — IP auth is stateless.
      // Just reject; the error UI in App.tsx will surface the message.
      console.error('[On-prem] Token refresh failed — IP may no longer be recognised:', refreshError);
    } else if (status === 401 || status === 403) {
      // Cloud: auth server rejected the stored credentials — re-configure
      console.error(`[Cloud] Credentials rejected (${status}) — clearing credentials and reloading`);
      DeviceConfigService.clearCredentials();
      window.location.reload();
    } else {
      console.error('[Cloud] Token refresh failed — keeping credentials, will retry:', refreshError);
    }
    return Promise.reject(refreshError);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Flight API
// ─────────────────────────────────────────────────────────────────────────────

const flightApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

flightApi.interceptors.request.use(
  async (config) => {
    const token = await getAuthToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error),
);

flightApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      return handle401Retry(originalRequest, flightApi);
    }
    return Promise.reject(error);
  },
);

// ─────────────────────────────────────────────────────────────────────────────
// DCMM API
// ─────────────────────────────────────────────────────────────────────────────

const dcmmApi = axios.create({
  baseURL: process.env.REACT_APP_DCMM_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

dcmmApi.interceptors.request.use(
  async (config) => {
    const token = await getAuthToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error),
);

dcmmApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      return handle401Retry(originalRequest, dcmmApi);
    }
    return Promise.reject(error);
  },
);

export { dcmmApi, flightApi };